import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Pagination,
  TextField,
  Tooltip,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { DataGrid } from "@mui/x-data-grid";
import {
  AnaliseProvaTecnicaRegistro,
  useGetAnaliseProvaTecnicaQuery,
} from "shared/features/api/analiseProvaTecnica/analiseProvaTecnicaSlice";

const ROW_HEIGHT = 52;
const HEADER_HEIGHT = 56;
// paginacao + espacamento do grid abaixo da tabela
const FOOTER_HEIGHT = 56;
// barra de rolagem horizontal da tabela
const SCROLLBAR_HEIGHT = 16;
const MIN_PAGE_SIZE = 5;

const formatNumber = (value: number) =>
  value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getAnaliseInicial = (situacao: string, integridade: string) => {
  if (situacao === "REPROVADO") {
    return { label: "Reprovado", color: "error" as const };
  }

  if (situacao === "APROVADO" && integridade === "SEM_RESSALVAS") {
    return { label: "Aprovado", color: "success" as const };
  }

  return { label: "Aprovado com ressalvas", color: "warning" as const };
};

export const ProvaTecnica: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(MIN_PAGE_SIZE);
  const [nomeCandidato, setNomeCandidato] = useState("");
  const [nomeCandidatoFiltro, setNomeCandidatoFiltro] = useState("");
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setNomeCandidatoFiltro(nomeCandidato.trim());
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [nomeCandidato]);

  useLayoutEffect(() => {
    const updatePageSize = () => {
      const top = gridRef.current?.getBoundingClientRect().top ?? 0;
      const available =
        window.innerHeight -
        top -
        HEADER_HEIGHT -
        FOOTER_HEIGHT -
        SCROLLBAR_HEIGHT;

      setPageSize(Math.max(Math.floor(available / ROW_HEIGHT), MIN_PAGE_SIZE));
    };

    updatePageSize();
    window.addEventListener("resize", updatePageSize);

    return () => window.removeEventListener("resize", updatePageSize);
  }, []);

  const { data, isLoading, isFetching } = useGetAnaliseProvaTecnicaQuery({
    pagina: page,
    tamanho: pageSize,
    nomeCandidato: nomeCandidatoFiltro || undefined,
  });

  useEffect(() => {
    if (data?.quantidadePaginas && page >= data.quantidadePaginas) {
      setPage(Math.max(data.quantidadePaginas - 1, 0));
    }
  }, [data?.quantidadePaginas, page]);

  const columns = [
    {
      field: "classificacao",
      headerName: "Classificação",
      flex: 0.5,
      minWidth: 110,
      align: "center" as const,
      headerAlign: "center" as const,
      renderCell: (params: any) => params.row.classificacao ?? "-",
    },
    {
      field: "candidatoNome",
      headerName: "Candidato",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "candidatoSemestreAtual",
      headerName: "Semestre",
      flex: 0.6,
      minWidth: 100,
    },
    {
      field: "provaNota",
      headerName: "Nota",
      flex: 0.6,
      minWidth: 100,
      renderCell: (params: any) => formatNumber(params.row.provaNota),
    },
    {
      field: "provaTempoGasto",
      headerName: "Tempo gasto",
      flex: 0.8,
      minWidth: 130,
    },
    {
      field: "analiseInicial",
      headerName: "Análise inicial",
      flex: 1,
      minWidth: 190,
      renderCell: (params: any) => {
        const analiseInicial = getAnaliseInicial(
          params.row.provaNotaCorteSituacao,
          params.row.provaIntegridade
        );

        return (
          <Chip
            label={analiseInicial.label}
            color={analiseInicial.color}
            size="small"
          />
        );
      },
    },
    {
      field: "provaLink",
      headerName: "Prova",
      flex: 0.5,
      minWidth: 110,
      align: "center" as const,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        const provaLink = params.row.provaLink as string | undefined;

        return (
          <Tooltip title={provaLink ? "Visualizar prova" : "Link da prova indisponível"}>
            <span>
              <IconButton
                disabled={!provaLink}
                onClick={() => window.open(provaLink, "_blank", "noopener,noreferrer")}
              >
                <OpenInNewIcon />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const rows =
    data?.elementos.map((registro: AnaliseProvaTecnicaRegistro) => ({
      id: registro.idAnaliseProvaTecnica,
      ...registro,
    })) ?? [];

  return (
    <Grid container spacing={2}>
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ mb: { xs: 1, sm: 0 } }}>
          Total de registros: <strong>{data?.totalElementos ?? 0}</strong>
        </Box>

        <TextField
          size="small"
          label="Nome do candidato"
          value={nomeCandidato}
          onChange={(event) => setNomeCandidato(event.target.value)}
          sx={{ width: { xs: "100%", sm: 280 } }}
        />
      </Grid>

      <Grid
        item
        xs={12}
        ref={gridRef}
        sx={{
          height: HEADER_HEIGHT + pageSize * ROW_HEIGHT + SCROLLBAR_HEIGHT + 2,
          width: "100%",
        }}
      >
        {isLoading || isFetching ? (
          <LinearProgress />
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={ROW_HEIGHT}
            headerHeight={HEADER_HEIGHT}
            pageSize={pageSize}
            rowsPerPageOptions={[pageSize]}
            hideFooter
            sx={{
              boxShadow: 2,
              "& .MuiDataGrid-cell": {
                fontSize: "14px",
              },
              "& .MuiDataGrid-columnHeaders": {
                fontSize: "16px",
                whiteSpace: "nowrap",
              },
            }}
          />
        )}
      </Grid>

      <Grid item xs={12} display="flex" justifyContent="center">
        <Pagination
          count={data?.quantidadePaginas ?? 1}
          color="primary"
          size="small"
          onChange={(_, newPage) => setPage(newPage - 1)}
          page={page + 1}
        />
      </Grid>
    </Grid>
  );
};
